// Pure helpers for drawing the recording itself on the recording map: which layers and
// sources are ours to clear before a redraw, the styled copy of the track, and its
// start/end markers. Kept free of Mapbox/DOM so Jest can exercise them directly.

type Position = number[];
interface Geometry { type: string; coordinates: any }
interface Feature {
  type?: string;
  geometry?: Geometry | null;
  properties?: Record<string, any> | null;
}
interface FeatureCollection { type?: string; features?: Feature[] }
export type RecordingGeoJson = (FeatureCollection & { coordinates?: undefined }) | (Geometry & { features?: undefined });

/** Layers (and same-named sources) RecordingMap adds straight onto the Mapbox map. */
export const RECORDING_LAYER_IDS = [
  'line',
  'recording-line',
  'recording-line-border',
  'recording-distance-markers',
  'recording-markers-start',
  'recording-markers-end',
] as const;

/** The connected hike's layers, which go through the LayerRegistry instead. */
export const RECORDING_HIKE_LAYER_IDS = [
  'recording-hike-lines-border-inactive',
  'recording-hike-lines-inactive',
  'recording-hike-lines-border',
  'recording-hike-lines',
] as const;

interface MapLike {
  getLayer(id: string): unknown;
  removeLayer(id: string): unknown;
  getSource(id: string): unknown;
  removeSource(id: string): unknown;
}
interface LayerRegistryLike { removeLayer(id: string): unknown }

/**
 * Clears everything a previous `addToMap` drew, so the recording (and its hike) can be
 * redrawn after a style change or a hike switch without duplicate-layer errors. Layers go
 * before sources because Mapbox refuses to drop a source a layer still uses.
 */
export function removeRecordingLayers(map: MapLike | null | undefined, layerRegistry?: LayerRegistryLike | null): void {
  if (map) {
    RECORDING_LAYER_IDS.forEach((id) => {
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    });
  }
  if (layerRegistry) {
    RECORDING_HIKE_LAYER_IDS.forEach((id) => layerRegistry.removeLayer(id));
  }
}

/**
 * The recording's track as a FeatureCollection whose features carry `routeStyle: 'route'`,
 * so the shared route-style expressions draw it as a main route. Accepts either a
 * FeatureCollection or a bare LineString; anything else yields an empty collection.
 */
export function transformRecordingForStyling(geojson: RecordingGeoJson | null | undefined): FeatureCollection {
  const features: Feature[] = [];
  if (geojson?.features) {
    geojson.features.forEach((feature) => {
      features.push({ ...feature, properties: { ...(feature.properties || {}), routeStyle: 'route' } });
    });
  } else if (geojson?.type === 'LineString') {
    features.push({ type: 'Feature', geometry: geojson as Geometry, properties: { routeStyle: 'route' } });
  }
  return { type: 'FeatureCollection', features };
}

/** The track's positions: the first feature's line (first part of a MultiLineString) or the bare LineString. */
function trackCoordinates(geojson: RecordingGeoJson | null | undefined): Position[] {
  if (!geojson) return [];
  if (geojson.features && geojson.features.length > 0) {
    const geometry = geojson.features[0].geometry;
    if (geometry?.type === 'LineString') return geometry.coordinates || [];
    if (geometry?.type === 'MultiLineString') return geometry.coordinates?.[0] || [];
    return [];
  }
  if (geojson.type === 'LineString') return geojson.coordinates || [];
  return [];
}

/**
 * Point features for the track's first and last positions, each tagged with its
 * `markerType`. A track shorter than two points has nowhere to put them.
 */
export function getRecordingStartEndMarkers(geojson: RecordingGeoJson | null | undefined): { startMarkers: Feature[]; endMarkers: Feature[] } {
  const coordinates = trackCoordinates(geojson);
  if (coordinates.length < 2) return { startMarkers: [], endMarkers: [] };
  const marker = (coords: Position, markerType: 'start' | 'end'): Feature => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: coords },
    properties: { markerType },
  });
  return {
    startMarkers: [marker(coordinates[0], 'start')],
    endMarkers: [marker(coordinates[coordinates.length - 1], 'end')],
  };
}

/** The hike planner opened where the recording map is looking, for "Create a hike from this recording". */
export function hikePlannerUrlFor(center: { lat: number; lng: number }, zoom: number): string {
  return `/hike-planner?lat=${center.lat}&lon=${center.lng}&zoom=${Math.round(zoom)}`;
}

/** The recording's report-issue endpoint, derived from its `/map` page path. */
export function reportIssueUrlFor(pathname: string): string {
  return pathname.replace(/\/map$/, '/report_issue');
}
